import {
  CatalogData,
  FilteredMaterialsByCategoriesI,
  Material,
  IProductConfigurationResult,
  IProductMaterialFilterEvent,
} from "ecom-types";

export type IUsePdpFilterOptions = {
  globalsSortOrder?: boolean;
};

export function useProductFilter(
  productData: Ref<CatalogData | null>,
  sizesSlugs: string[] = [],
  options?: IUsePdpFilterOptions,
) {
  const _options = options || {};
  const result = ref<IProductConfigurationResult>({});
  const additionalOptions = ref<IProductConfigurationResult>({});
  const clickOnAnyFilter = ref(false);
  const currentSku = ref(
    productData.value?.selected_config?.configuration_id ||
    productData.value?.variant_plp_image?.sku ||
      productData.value?.default_config?.vendor_configuration_id ||
      productData.value?.default_sku ||
      "",
  );
  const productMaterialCategories = (productData.value as any)?.product_material_categories || productData.value?.material_categories;
  const productMaterials = (productData.value as any)?.product_materials || productData.value?.materials;

  const generalStore = useGeneralStore();

  const filteredMaterialsByCategories = computed(
    (): FilteredMaterialsByCategoriesI[] => {
      if (!productData.value) {
        return [];
      }

      const materialsByCategories =
        productMaterialCategories?.reduce(
          (acc: FilteredMaterialsByCategoriesI[], el) => {
            const currentCategory =
              generalStore.bootstrap?.material_categories?.find(
                (it) => it.id === el.id,
              );

            if (currentCategory) {
              acc.push({
                ...currentCategory,
                materials: (productMaterials || []).reduce(
                  (acc: Material[], material) => {
                    if (material.material_category_id === el.id) {
                      acc.push({
                        ...material,
                      });
                    }
                    return acc;
                  },
                  [],
                ),
              });
            }
            return acc;
          },
          [],
        ) || [];

      return _options.globalsSortOrder
        ? materialsByCategories.sort(
            (a, b) => (a?.sort_order || 0) - (b?.sort_order || 0),
          )
        : materialsByCategories;
    },
  );

  const disabledMaterials = computed(() => {
    if (!productData.value || !(productData.value as any).materials_disabled_pairs) return [];
    const disabledMaterialsSet = new Set();
    const selectedMaterialsSet = new Set(
      [
        ...Object.values(result.value),
        ...Object.values(additionalOptions.value),
      ]
        .filter(Boolean)
        .map((material) => material?.id),
    );

    (productData.value as any).materials_disabled_pairs.forEach((pair: any) => {
      if (selectedMaterialsSet.has(pair.material_id)) {
        disabledMaterialsSet.add(pair.material_with_id);
      }
    });

    return Array.from(disabledMaterialsSet);
  });

  const allSelectedMaterials = computed(() => {
    return {
      ...result.value,
      ...additionalOptions.value,
    };
  });

  const hasUnavailableMaterial = computed(() => {
    return [
      ...Object.values(result.value),
      ...Object.values(additionalOptions.value),
    ]
      .filter(Boolean)
      .some((material) => disabledMaterials.value.includes(material?.id));
  });

  const filteredMaterialsByCategoriesWithDisabled = computed(() => {
    return filteredMaterialsByCategories.value.map((category) => {
      return {
        ...category,
        materials: category.materials.map((material) => ({
          ...material,
          disabled: disabledMaterials.value.includes(material.id),
        })),
      };
    });
  });

  const filteredMaterialsByCategoriesWithDisabledPlp = computed(() => {
    if (!productData.value?.material_categories_plp_hover_ids) {
      return [];
    }
    
    return filteredMaterialsByCategoriesWithDisabled.value.filter((category) =>
      Boolean(productData.value?.material_categories_plp_hover_ids?.includes(category.id))
    );
  });

  function onFilterClick({ slug, item }: IProductMaterialFilterEvent) {
    if (!item) return;

    clickOnAnyFilter.value = true;

    result.value[slug] = item as any;
    checkAvailabilityMaterials();
    setConfigurationValues();
  }

  function setConfigurationValues() {
    const newSku = Object.values(result.value).reduce((acc, el: any) => {
      return acc + "-" + el.sku;
    }, productData.value?.vendor_model as string);

    const materials = Object.values(additionalOptions.value).reduce(
      (acc, value) => {
        if (value) {
          acc.push(value.sku);
        }
        return acc;
      },
      [] as string[],
    );

    currentSku.value = newSku;
  }

  function onOptionsFilterClick({ item, slug }: IProductMaterialFilterEvent) {
    if (!item) return;

    clickOnAnyFilter.value = true;
    additionalOptions.value[slug] = item as any;
    checkAvailabilityMaterials();
    setConfigurationValues();
  }

  function checkAvailabilityMaterials() {
    function updateAvailability(results: IProductConfigurationResult) {
      Object.entries(results).forEach(([key, value]) => {
        if (value) {
          const materialCategory =
            filteredMaterialsByCategoriesWithDisabled.value.find(
              (category) => category.id === value.material_category_id,
            );
          const foundMaterial = materialCategory?.materials.find(
            (material) => material.id === value.id,
          );
          if (foundMaterial?.disabled) {
            const newValue = materialCategory?.materials.find(
              (material) => !material.disabled,
            );
            if (newValue) {
              results[key] = newValue;
            }
          }
        }
      });
    }

    updateAvailability(result.value);
    updateAvailability(additionalOptions.value);
  }

  function setResultData() {
    productMaterialCategories?.forEach((el) => {
      const currentMaterials = filteredMaterialsByCategories.value?.length
        ? filteredMaterialsByCategories.value?.find(
            (item) => item.id === el?.id,
          )?.materials
        : [];

     const productConfigurationMaterials = (productData.value?.selected_config as any)?.materials || (productData.value?.variant_plp_image as any)?.materials || (productData.value?.default_config as any)?.materials;

      const material = currentMaterials?.find((item) =>
          productConfigurationMaterials?.includes(item.id),
      );

      if (el?.slug && material && el.sku_enabled) {
        result.value[el?.slug] = material as any;
      } else if (currentMaterials?.length) {
        if (sizesSlugs.includes(el.slug)) {
          additionalOptions.value[el?.slug] = null;
        } else {
          additionalOptions.value[el?.slug] = currentMaterials?.[0] as any || null;
        }
      }
    });
    checkAvailabilityMaterials();
    setConfigurationValues();
  }

  return {
    filteredMaterialsByCategories,
    filteredMaterialsByCategoriesWithDisabled,
    filteredMaterialsByCategoriesWithDisabledPlp,
    result,
    additionalOptions,
    clickOnAnyFilter,
    currentSku,
    setResultData,
    onFilterClick,
    onOptionsFilterClick,
    disabledMaterials,
    hasUnavailableMaterial,
    checkAvailabilityMaterials,
    allSelectedMaterials,
  };
}

